import type { BaseField, FieldData } from '../fields/BaseField';
import type { Security } from '../types';
import type { OrderStrategy, Parameter } from '../types/OrderStrategy';
import { ParameterUnitType } from '../types/OrderStrategy';
import { ParameterTypeEnum } from '../types/OrderStrategy.types';

/**
 * Gets the unit suffix for display (converts BasisPoints to bps, etc.)
 *
 * This function converts parameter unit type names to their display format:
 * - BasisPoints -> 'bps'
 * - PriceIncrements -> 'increments'
 * - PriceUnits -> security quote currency (e.g., 'USD')
 *
 * @param optionName - The unit option name (e.g., 'BasisPoints')
 * @param security - Optional security object to get quote currency for PriceUnits
 * @returns The display suffix for the unit, or undefined if no conversion exists
 *
 * @example
 * ```typescript
 * getUnitSuffix('BasisPoints', security) // returns 'bps'
 * getUnitSuffix('PriceUnits', security) // returns 'USD' (or security.QuoteCurrency)
 * getUnitSuffix('SomeOtherUnit', security) // returns undefined
 * ```
 */
export function getUnitSuffix(optionName: string | undefined, security: Security | undefined): string | undefined {
  if (!optionName) {
    return undefined;
  }

  switch (optionName) {
    case ParameterUnitType.BasisPoints:
      return 'BPS';
    case ParameterUnitType.PriceIncrements:
      return 'Increments';
    case ParameterUnitType.PriceUnits:
      return security?.QuoteCurrency;
    default:
      return undefined;
  }
}

/**
 * Gets the unit display value for a parameter that has units
 *
 * This function retrieves the current unit value for a parameter and converts it
 * to its display format using getUnitSuffix. It handles fallbacks to default values
 * and option names.
 *
 * @param parameter - The parameter that may have units
 * @param strategy - The strategy containing all parameters
 * @param fields - The parameter fields containing current values
 * @param security - Optional security object for unit conversion
 * @returns The display value for the unit (e.g., 'bps', 'USD'), or undefined if no units
 *
 * @example
 * ```typescript
 * const unitDisplay = getUnitDisplayValue(priceParam, strategy, fields, security);
 * // Returns 'bps' if unit is BasisPoints, 'USD' if PriceUnits, etc.
 * ```
 */
export function getUnitDisplayValue(
  parameter: Parameter,
  strategy: OrderStrategy,
  fields: { [key: string]: BaseField<FieldData, any> },
  security: Security | undefined
): string | undefined {
  if (!parameter.unitsParam || !strategy) {
    return undefined;
  }

  const unitsParameter = strategy.Parameters.find(
    p => p.Name === parameter.unitsParam && p.Type === ParameterTypeEnum.Units
  );

  if (!unitsParameter) {
    return undefined;
  }

  const unitsField = fields[unitsParameter.Name];

  // Get the selected unit value, with fallbacks
  let unitValue: string | undefined;
  if (!unitsField?.value && unitsParameter.DefaultValue) {
    unitValue =
      typeof unitsParameter.DefaultValue === 'string'
        ? unitsParameter.DefaultValue
        : unitsParameter.options?.at(0)?.name;
  } else {
    unitValue = unitsField?.value || unitsParameter.options?.at(0)?.name;
  }

  // Convert to display format (e.g., BasisPoints -> bps)
  return getUnitSuffix(unitValue, security) || unitValue;
}
